import React from "react"
import ReactApexChart from "react-apexcharts"

const HeatMapComponent = ({
	data,
	customTooltip,
	colorRanges = [],
	baseColor = "#6955EA",
	height = 200,
	componentHeight = null,
	xAxisPosition = "bottom",
	categories = null,
	seriesNames = null,
	invertYAxis = true
}) => {
	// Transform data into ApexCharts format
	const processData = rawData => {
		// Get unique hours (y-axis values)
		let uniqueHours = [...new Set(rawData.map(item => item.hour))]

		// Determine hours order
		let hours
		if (seriesNames) {
			// If specific series names are provided, use their exact order
			hours = seriesNames
		} else if (invertYAxis) {
			// Otherwise sort normally and optionally invert
			hours = uniqueHours.sort((a, b) => b.localeCompare(a))
		} else {
			hours = uniqueHours.sort()
		}

		// Get unique dates (x-axis values)
		let dates
		if (categories) {
			// If specific categories are provided, use them
			dates = categories
		} else {
			// Otherwise extract from data
			dates = [...new Set(rawData.map(item => item.date))].sort()
		}

		// Create series data where each series represents a y-axis value
		// Important: Maintain the exact order from the hours array
		const series = hours.map(hour => {
			return {
				name: `${hour}`,
				data: dates.map(date => {
					// Find data point matching this hour and date
					const dataPoint = rawData.find(d => d.date === date && d.hour === hour)
					return {
						x: date,
						y: dataPoint ? dataPoint.value : 0,
						// Store original values for tooltip
						...dataPoint
					}
				})
			}
		})

		return { series, hours, dates }
	}

	const { series, dates } = processData(data)

	const options = {
		chart: {
			height: height,
			type: "heatmap",
			toolbar: {
				show: false
			},
			animations: {
				speed: 500
			},
			fontFamily: "Manrope, sans-serif"
		},
		legend: {
			show: false
		},
		dataLabels: {
			enabled: false
		},
		colors: [baseColor],
		tooltip: {
			custom:
				customTooltip ||
				function ({ series, seriesIndex, dataPointIndex, w }) {
					// Simple default tooltip if none provided
					if (!series || !series[seriesIndex] || dataPointIndex === undefined) {
						return ""
					}
					const value = series[seriesIndex][dataPointIndex]
					return `<div style="padding: 8px;">${value}</div>`
				}
		},
		xaxis: {
			type: "category",
			categories: dates,
			position: xAxisPosition,
			labels: {
				rotate: 0,
				rotateAlways: false,
				style: {
					colors: "#352D66",
					fontFamily: "Manrope, sans-serif",
					fontSize: "12px"
				},
				formatter: function (value) {
					// Only apply automatic label filtering if there are many labels
					if (dates.length <= 10) {
						return value
					} else {
						// Show labels at regular intervals for readability
						const allLabels = dates
						const totalDates = allLabels.length

						// Calculate which indices to show (0, 25%, 50%, 75%, 100% positions)
						const indicesToShow = [
							0,
							Math.floor(totalDates * 0.25),
							Math.floor(totalDates * 0.5),
							Math.floor(totalDates * 0.75),
							totalDates - 1
						]

						const currentIndex = allLabels.indexOf(value)
						return indicesToShow.includes(currentIndex) ? value : ""
					}
				}
			},
			axisBorder: {
				show: true
			},
			axisTicks: {
				show: true
			}
		},
		yaxis: {
			labels: {
				style: {
					colors: "#352D66",
					fontFamily: "Manrope, sans-serif",
					fontSize: "12px"
				}
			},
			axisBorder: {
				show: false
			},
			axisTicks: {
				show: false
			}
		},
		grid: {
			show: false
		},
		stroke: {
			width: 2,
			colors: ["#ffffff"]
		},
		plotOptions: {
			heatmap: {
				radius: 4,
				enableShades: true,
				shadeIntensity: 0.7,
				colorScale: {
					ranges: colorRanges
				},
				columnWidth: "98%"
			}
		}
	}

	return (
		<div className="w-full h-full" style={{ width: "100%" }}>
			<ReactApexChart
				options={options}
				series={series}
				type="heatmap"
				height={height}
				width="100%"
			/>
		</div>
	)
}

export default HeatMapComponent
